/**
 * The server (a Supabase project) this copy of Nexus Open talks to. There is
 * no default: without one, Nexus Open works on this device only and sends
 * nothing over the network. The publishable key is meant to be public
 * (row-level security in the database protects each user's data).
 *
 * Where it comes from, first match wins, read once at start (`loadServerConfig`):
 * 1. a connection made in the app (Settings → Account, or an invite link), kept on this device;
 * 2. `/config.json` next to a self-hosted web app, so one build serves every team;
 * 3. the build: VITE_SUPABASE_URL / VITE_SUPABASE_KEY.
 */

export interface ServerConfig {
  url: string;
  key: string;
  /** Postgres/PostgREST schema this project's tables live in (default: "public"). */
  schema: string;
  /** Show "create account" (off on a team project: the owner invites colleagues). */
  allowSignup: boolean;
  /** The web app may be used without an account (data stays in that browser). */
  allowLocal: boolean;
  /** Web Push application server key (public; the private half is a secret of the project). */
  vapidPublicKey: string;
  /** The hosted web app, if any: password and invite links lead here (also from the desktop app). */
  webUrl: string;
  source: 'device' | 'site' | 'build' | 'none';
}

/** The `/config.json` of a self-hosted web app (all fields optional). */
export interface SiteConfig {
  supabaseUrl?: string;
  supabaseKey?: string;
  /** Only needed when this project shares a Supabase instance with other apps. */
  supabaseSchema?: string;
  allowSignup?: boolean;
  allowLocal?: boolean;
  vapidPublicKey?: string;
  webUrl?: string;
}

const DEVICE_KEY = 'nexus-open-server';

const env = import.meta.env;
const fromBuild: SiteConfig = {
  supabaseUrl: env.VITE_SUPABASE_URL,
  supabaseKey: env.VITE_SUPABASE_KEY,
  supabaseSchema: env.VITE_SUPABASE_SCHEMA,
  allowSignup: env.VITE_ALLOW_SIGNUP === 'true',
  allowLocal: env.VITE_ALLOW_LOCAL === 'true',
  vapidPublicKey: env.VITE_VAPID_PUBLIC_KEY,
  webUrl: env.VITE_WEB_URL,
};

type DeviceServer = { url: string; key: string; schema?: string; webUrl?: string };

export function resolveConfig(
  device: DeviceServer | null,
  site: SiteConfig | null,
  build: SiteConfig,
): ServerConfig {
  const pick = <K extends keyof SiteConfig>(k: K) => site?.[k] ?? build[k];
  const base = {
    allowSignup: !!pick('allowSignup'),
    allowLocal: !!pick('allowLocal'),
    vapidPublicKey: pick('vapidPublicKey') ?? '',
    webUrl: pick('webUrl') ?? '',
  };
  if (device?.url && device.key)
    return {
      ...base,
      webUrl: device.webUrl || base.webUrl,
      url: device.url,
      key: device.key,
      schema: device.schema || 'public',
      source: 'device',
    };
  if (site?.supabaseUrl && site.supabaseKey)
    return {
      ...base,
      url: site.supabaseUrl,
      key: site.supabaseKey,
      schema: pick('supabaseSchema') || 'public',
      source: 'site',
    };
  if (build.supabaseUrl && build.supabaseKey)
    return {
      ...base,
      url: build.supabaseUrl,
      key: build.supabaseKey,
      schema: pick('supabaseSchema') || 'public',
      source: 'build',
    };
  return { ...base, url: '', key: '', schema: 'public', source: 'none' };
}

function readDevice(): DeviceServer | null {
  try {
    const raw = localStorage.getItem(DEVICE_KEY);
    return raw ? (JSON.parse(raw) as DeviceServer) : null;
  } catch {
    return null;
  }
}

async function readSite(): Promise<SiteConfig | null> {
  // The desktop app has no site; the web app only asks its own origin.
  if (typeof window === 'undefined' || '__TAURI_INTERNALS__' in window) return null;
  try {
    const res = await fetch('/config.json', { cache: 'no-store' });
    if (!res.ok || !res.headers.get('content-type')?.includes('json')) return null;
    return (await res.json()) as SiteConfig;
  } catch {
    return null;
  }
}

let current: ServerConfig = resolveConfig(readDevice(), null, fromBuild);

/** Read the server settings once, before the app starts. */
export async function loadServerConfig(): Promise<ServerConfig> {
  current = resolveConfig(readDevice(), await readSite(), fromBuild);
  return current;
}

export const server = (): ServerConfig => current;

/**
 * Where links in mails (invite, password reset) lead: the hosted web app. The
 * web app itself is one too when none is set; the desktop app can't open links.
 */
export function linkTarget(): string {
  if (current.webUrl) return current.webUrl;
  if (typeof window === 'undefined' || '__TAURI_INTERNALS__' in window) return '';
  return `${window.location.origin}/`;
}

/** Whether a server is set up at all: sync, sign-in, AI and push need one. */
export const serverConfigured = (): boolean => !!current.url && !!current.key;

/** Keep a connection on this device (null: forget it). The app reloads afterwards. */
export function saveDeviceServer(conn: DeviceServer | null): void {
  if (conn) localStorage.setItem(DEVICE_KEY, JSON.stringify(conn));
  else localStorage.removeItem(DEVICE_KEY);
}
